/**
 * Simple in-memory cache for ERDDAP responses
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export class ERDDAPCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private readonly defaultTTL: number;
  private readonly maxSize: number;
  private hits: number = 0;
  private misses: number = 0;

  constructor(defaultTTL: number = 5 * 60 * 1000, maxSize: number = 100) { // 5 minutes default TTL
    this.defaultTTL = defaultTTL;
    this.maxSize = maxSize;
  }

  /**
   * Generate cache key from request parameters
   */
  static generateKey(
    dataset: string,
    bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number },
    resolution: number,
    date?: Date
  ): string {
    const dateStr = date ? date.toISOString().split('T')[0] : 'latest';
    return `${dataset}_${bounds.minLat}_${bounds.maxLat}_${bounds.minLon}_${bounds.maxLon}_${resolution}_${dateStr}`;
  }

  /**
   * Get cached data if available and not expired
   */
  get<T = unknown>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      this.misses++;
      return null;
    }

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      this.misses++;
      return null;
    }

    this.hits++;
    return entry.data as T;
  }

  /**
   * Store data in cache
   */
  set<T = unknown>(key: string, data: T, ttl?: number): void {
    const now = Date.now();
    const expiresAt = now + (ttl || this.defaultTTL);

    // If cache is full, remove oldest entries
    if (this.cache.size >= this.maxSize) {
      const entriesToRemove = Math.max(1, Math.floor(this.maxSize * 0.1)); // Remove 10%
      const sortedEntries = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      for (let i = 0; i < entriesToRemove; i++) {
        this.cache.delete(sortedEntries[i][0]);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt
    });
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; hits: number; misses: number; hitRate: number } {
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? this.hits / total : 0;
    return {
      size: this.cache.size,
      hits: this.hits,
      misses: this.misses,
      hitRate
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
      }
    }
  }
}

import { getConfigValue } from '../config/erddapConfig';

// Create singleton instance
export const erddapCache = new ERDDAPCache(
  getConfigValue('CACHE_TTL_MS'),
  getConfigValue('MAX_CACHE_ENTRIES')
);

// Run cleanup every minute
setInterval(() => {
  erddapCache.cleanup();
}, 60 * 1000);